import React, {
  KeyboardEvent as ReactKeyboardEvent,
  ReactNode,
  forwardRef,
  useCallback,
  useEffect,
} from 'react';

import cx from 'classnames';

import { useCallbackRef, useId } from '~shared/hooks';
import { normalizeKey } from '~shared/utils/normalize-keycode';

import { CLEAR_SEARCH, CLOSE_MENU, GO_TO_ITEM, SEARCH } from './actions';
import { useDisposables } from './hooks/useDisposables';
import { useMenuContext } from './MenuContext';
import { disposables } from './utils/disposables';
import { Focus } from './utils/getIndex';

interface Props {
  children: ReactNode;
  className?: string;
  style?: any;
}

/**
 * Renders a div containing the MenuItems when the MenuButton is triggered.
 * This div is unstyled by default, used only for a11y landmarks and handling
 * keyboard events during navigation.
 *
 * Styles can be added via the `className` prop passed through to the div itself
 * or using a child container within this component.
 *
 * @see https://www.w3.org/TR/wai-aria-practices-1.1/#menu
 */
export const MenuList = forwardRef<HTMLDivElement, Props>(function MenuList(
  { children, className, style }: Props,
  ref
) {
  const [state, dispatch] = useMenuContext('MenuList');
  const listRef = useCallbackRef(state.listRef, ref);
  const id = `menu-list-${useId()}`;
  const searchDisposables = useDisposables();

  useEffect(() => {
    const container = state.listRef.current;

    if (!container) return;
    if (state.isOpen !== true) return;
    if (container === document.activeElement) return;

    container.focus({ preventScroll: true });
  }, [state.listRef, state.isOpen]);

  const handleKeyDown = useCallback(
    (event: ReactKeyboardEvent<HTMLDivElement>) => {
      searchDisposables.dispose();
      const targetKey = normalizeKey(event);

      switch (targetKey) {
        case ' ':
          if (state.searchQuery !== '') {
            event.preventDefault();
            event.stopPropagation();
            return dispatch({ type: SEARCH, value: event.key });
          }

        // eslint-disable-next-line no-fallthrough
        case 'Enter':
          event.preventDefault();
          event.stopPropagation();
          // @todo Only CLOSE if item is not a MenuOption.
          dispatch({ type: CLOSE_MENU });

          if (state.activeItemIndex !== null) {
            const { id } = state.items[state.activeItemIndex];
            document.getElementById(id)?.click();
          }

          disposables().nextFrame(() =>
            state.buttonRef.current?.focus({ preventScroll: true })
          );
          break;

        case 'ArrowUp':
          event.preventDefault();
          event.stopPropagation();
          return dispatch({ type: GO_TO_ITEM, focus: Focus.PREVIOUS });

        case 'ArrowDown':
          event.preventDefault();
          event.stopPropagation();
          return dispatch({ type: GO_TO_ITEM, focus: Focus.NEXT });

        case 'Home':
        case 'PageUp':
          event.preventDefault();
          event.stopPropagation();
          return dispatch({ type: GO_TO_ITEM, focus: Focus.FIRST });

        case 'End':
        case 'PageDown':
          event.preventDefault();
          event.stopPropagation();
          return dispatch({ type: GO_TO_ITEM, focus: Focus.LAST });

        case 'Escape':
          event.preventDefault();
          event.stopPropagation();
          dispatch({ type: CLOSE_MENU });
          disposables().nextFrame(() =>
            state.buttonRef.current?.focus({ preventScroll: true })
          );
          break;

        case 'Tab':
          event.preventDefault();
          event.stopPropagation();
          break;

        default:
          if (event.key.length === 1) {
            dispatch({ type: SEARCH, value: event.key });
            searchDisposables.setTimeout(
              () => dispatch({ type: CLEAR_SEARCH }),
              350
            );
          }
          break;
      }
    },
    [searchDisposables, state, dispatch]
  );

  const handleKeyUp = useCallback(
    (event: ReactKeyboardEvent<HTMLDivElement>) => {
      switch (normalizeKey(event)) {
        /**
         * In Firefox, `preventDefault()` in handleKeyDown for the
         * Space key doesn't cancel the keyUp event, which triggers
         * a click.
         */
        case ' ':
          event.preventDefault();
          break;
      }
    },
    []
  );

  const menuClass = cx(
    !state.isOpen && 'invisible opacity-0',
    className,
    'z-50'
  );

  const listProps = {
    className: menuClass,
    'aria-activedescendant':
      state.activeItemIndex === null
        ? undefined
        : state.items[state.activeItemIndex]?.id,
    'aria-labelledby': state.buttonRef.current?.id,
    id,
    onKeyDown: handleKeyDown,
    onKeyUp: handleKeyUp,
    role: 'menu',
    tabIndex: 0,
    hidden: state.isOpen ? undefined : true,
    ref: listRef,
  };

  return (
    <div style={style} {...listProps}>
      {children}
    </div>
  );
});
